import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { UserCheck } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { usersAcceptInvitation, usersInvitationPreview } from '@/shared/api/generated/users/users';
import { ApiError } from '@/shared/api/http';
import { t } from '@/shared/i18n';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { toast } from '@/shared/ui/Toast';

import styles from '../auth.module.scss';

/**
 * Приём приглашения по ссылке.
 *
 * Открыт без входа: человек ещё не завёлся, он как раз по этой ссылке и
 * собирается это сделать. Поэтому экран живёт вне защищённой части и сам
 * решает, что показать — форму, отказ или предложение просто войти.
 */
export function InvitePage() {
  const { token } = useParams({ from: '/public/invite/$token' });
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const query = useQuery({
    queryKey: ['invitation', token],
    queryFn: async () => {
      const response = await usersInvitationPreview(token);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на просмотр приглашения');
      }

      return response.data;
    },
    retry: false,
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);
    setBusy(true);

    usersAcceptInvitation(token, { name: name.trim(), password })
      .then(() => {
        toast.success(t.invite.accepted, { description: t.invite.acceptedHint });
        void navigate({ to: '/login' });
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.detail : t.invite.failed);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  if (query.isLoading) {
    return <p className={styles['text']}>{t.invite.checking}</p>;
  }

  if (query.isError || query.data === undefined) {
    return (
      <div className={styles['card']}>
        <h1 className={styles['title']}>{t.invite.brokenTitle}</h1>
        <p className={styles['text']}>{t.invite.brokenText}</p>
      </div>
    );
  }

  const invitation = query.data;

  return (
    <form className={styles['card']} onSubmit={submit} noValidate>
      <header className={styles['head']}>
        <h1 className={styles['title']}>{t.invite.title}</h1>
        <p className={styles['text']}>
          {t.invite.into} <b>{invitation.tenant_name}</b>{' '}
          {t.invite.asRole[invitation.role] ?? invitation.role}
          {invitation.site_name !== undefined && invitation.site_name !== null && (
            <>
              {' '}
              {t.invite.onSite} <b>{invitation.site_name}</b>
            </>
          )}
          .
        </p>
        <p className={styles['mono']}>
          {t.invite.address}: {invitation.email}
        </p>
      </header>

      {invitation.needs_password ? (
        <>
          <Field label={t.invite.name} hint={t.invite.nameHint}>
            <Input
              value={name}
              autoFocus
              placeholder={t.invite.namePlaceholder}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          </Field>

          <Field label={t.invite.newPassword} required error={error}>
            <Input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          </Field>
        </>
      ) : (
        <Field label={t.invite.password} hint={t.invite.existingHint} error={error}>
          <Input
            type="password"
            autoComplete="current-password"
            value={password}
            placeholder={t.invite.optional}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
        </Field>
      )}

      <Button type="submit" size="lg" fullWidth loading={busy} iconLeft={<UserCheck size={16} />}>
        {t.invite.accept}
      </Button>
    </form>
  );
}
